import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ApiError } from '@/lib/api/errors'
import { saveCourtOperatingHours } from '@/lib/venue/operatingHourApi'
import type {
  CourtOperatingHourSaveInput,
  CourtOperatingHourView,
} from '@/lib/venue/operatingHourTypes'

import { courtOperatingHoursQueryKey } from './useCourtOperatingHours'
import { courtsQueryKey } from './useCourts'

/**
 * 코트 운영시간 저장 (#600 §2).
 *
 * 성공 시:
 *  ① 응답(= `GET` 과 동일한 View)을 이 화면의 쿼리 캐시에 **직접 반영**한다 — 재조회하지 않는다
 *     (Reader 복제 지연을 피한다, #600 D10).
 *  ② 코트 목록 쿼리는 **무효화**한다 — 목록의 운영시간 요약·예약단위·최대예약시간이 이 화면과
 *     같은 값을 공유하지만 응답 스키마가 달라(`CourtListView` vs `CourtOperatingHourView`) 직접
 *     반영할 수 없다(#600 §4, 설계 §2-2 자율 결정).
 */
export function useSaveCourtOperatingHours(venueId: string, courtId: number) {
  const queryClient = useQueryClient()

  return useMutation<CourtOperatingHourView, ApiError, CourtOperatingHourSaveInput>({
    mutationFn: (input) => saveCourtOperatingHours(venueId, courtId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(courtOperatingHoursQueryKey(venueId, courtId), data)
      void queryClient.invalidateQueries({ queryKey: courtsQueryKey(venueId) })
    },
  })
}
